import React from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

interface ConfigurationsSectionProps {
  weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT";
  weightsApplied: boolean;
  isRefetchingData: boolean;
  dataSorting: "ascending" | "descending" | "none" | "custom";
  onWeightChange: (
    weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT",
  ) => void;
  onDataSortingChange: (
    sorting: "ascending" | "descending" | "none" | "custom",
  ) => void;
}

const ConfigurationsSection: React.FC<ConfigurationsSectionProps> = ({
  weight,
  weightsApplied,
  isRefetchingData,
  dataSorting,
  onWeightChange,
  onDataSortingChange,
}) => {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
        Configurations
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth size="small">
          <FormLabel className="text-[12px] text-[#262626]">
            Weight Selection
          </FormLabel>
          <Select
            value={weight}
            onChange={(e) =>
              onWeightChange(
                e.target.value as "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT",
              )
            }
            disabled={isRefetchingData}
          >
            <MenuItem value="DISABLED">Disabled</MenuItem>
            <MenuItem value="DATASET_DEFAULT">Use Dataset Default</MenuItem>
          </Select>
        </FormControl>

        {weight === "DISABLED" && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            sx={{
              fontSize: "0.75rem",
              "& .MuiAlert-message": { fontSize: "0.75rem" },
            }}
          >
            Weights disabled: unweighted counts shown.
          </Alert>
        )}

        {weight === "DATASET_DEFAULT" && !weightsApplied && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            sx={{
              fontSize: "0.75rem",
              "& .MuiAlert-message": { fontSize: "0.75rem" },
            }}
          >
            No default weight configured for this dataset. Showing unweighted
            counts.
          </Alert>
        )}

        <FormControl fullWidth size="small">
          <FormLabel className="text-[12px] text-[#262626]">
            Data Sorting
          </FormLabel>
          <Select
            value={dataSorting}
            onChange={(e) =>
              onDataSortingChange(
                e.target.value as "ascending" | "descending" | "none" | "custom",
              )
            }
          >
            <MenuItem value="none">Dataset Value Order</MenuItem>
            <MenuItem value="ascending">Ascending %</MenuItem>
            <MenuItem value="descending">Descending %</MenuItem>
            <MenuItem value="custom">Custom order</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default ConfigurationsSection;
